/**
 * RTWO Cryptography & Identity Engine — Identity Registration Service
 */

import { CRYPTO_SERVICE_IDS } from '../domain-models';
import type { IdentityId, IdentityType, RTWOIdentity } from '../domain-models';
import type {
  IIdentityRegistrationService,
  IdentityRegistrationRequest,
  IdentityRegistrationResult,
} from '../service-interfaces';
import type { CryptoRepositories } from '../repositories/crypto-repositories';
import type { IdentityBackendRegistry } from '../registry/provider-registries';
import { CryptoEventTypes } from '../events/crypto-event-bus';
import { BaseCryptoService, type CryptoServiceContext } from './base-crypto-service';

export class IdentityRegistrationService
  extends BaseCryptoService
  implements IIdentityRegistrationService
{
  readonly serviceId = CRYPTO_SERVICE_IDS.IDENTITY_REGISTRATION;
  readonly serviceName = 'Identity Registration Service';
  readonly description = 'Registers identities linked to key pairs via Identity Backend.';
  readonly layer = 'R7' as const;
  readonly integrations = ['System Registry', 'R7 Enterprise Services'];
  readonly requiresKeyProvider = true;
  readonly requiresIdentityBackend = true;

  constructor(
    context: CryptoServiceContext,
    private readonly repositories: CryptoRepositories,
    private readonly identityBackendRegistry: IdentityBackendRegistry
  ) {
    super(context);
  }

  async registerIdentity(
    request: IdentityRegistrationRequest
  ): Promise<IdentityRegistrationResult> {
    const keyPair = this.repositories.keyPairs.findById(request.keyPairId);
    if (!keyPair || !this.identityBackendRegistry.isConfigured()) {
      return {
        success: false,
        identityId: null,
        error: 'Identity Backend not connected. No identities registered.',
        isPlaceholder: true,
        source: 'Identity Backend — Not Connected',
      };
    }

    const result = await this.identityBackendRegistry
      .getActiveBackend()
      .registerIdentity(request.keyPairId, request.identityType, request.metadata);

    if (!result?.identity || !result.identityId) {
      return {
        success: false,
        identityId: null,
        error: 'Identity Backend did not register an identity.',
        isPlaceholder: true,
        source: 'Identity Backend — Not Connected',
      };
    }

    this.repositories.identities.save(result.identity);
    this.context.eventBus.emit(CryptoEventTypes.IDENTITY_CREATED, result.identityId, {
      identityType: request.identityType,
    });

    return {
      success: false,
      identityId: null,
      error: 'Identity ingested but interface remains placeholder until backend certification.',
      isPlaceholder: true,
      source: 'Identity Backend — Not Connected',
    };
  }

  getIdentity(identityId: IdentityId): RTWOIdentity | null {
    if (!this.identityBackendRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.identities.findById(identityId);
  }

  getIdentitiesByType(identityType: IdentityType): RTWOIdentity[] {
    if (!this.identityBackendRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.identities.findByType(identityType);
  }

  getIdentityCount(): number | null {
    if (!this.identityBackendRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.identities.count();
  }
}
